/**
 * Workspace Context Hook
 * Provides workspace context and management for multi-tenant applications
 */

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useUser } from '../contextapi/UserContext';
import { supabase } from '../utils/supabaseClient';

export interface WorkspaceContextType {
  currentWorkspace: Workspace | null;
  availableWorkspaces: Workspace[];
  isLoading: boolean;
  error: string | null;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  refreshWorkspaces: () => Promise<void>;
  createWorkspace: (name: string, description?: string) => Promise<Workspace>;
}

export interface Workspace {
  id: string;
  name: string;
  description?: string;
  role: string;
  created_at: string;
  updated_at: string;
  is_default: boolean;
  last_accessed?: string;
  member_count?: number;
  plugin_status?: Record<string, 'healthy' | 'error' | 'loading'>;
}

// Create workspace context
const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

// Workspace Provider Component
export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [availableWorkspaces, setAvailableWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load user workspaces
  const loadWorkspaces = useCallback(async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get user organizations
      const { data: orgData, error: orgError } = await supabase.rpc('get_user_organizations');
      
      if (orgError) throw orgError;

      // Get workspace preferences
      const { data: prefData, error: prefError } = await supabase.rpc('get_user_workspace_preferences');
      
      if (prefError) throw prefError;

      // Combine and enrich workspace data
      const enrichedWorkspaces = await Promise.all(
        (orgData || []).map(async (org: any) => {
          const preferences = prefData?.find((p: any) => p.tenant_id === org.id);
          
          return {
            id: org.id,
            name: org.name,
            description: org.description,
            role: org.role,
            created_at: org.created_at,
            updated_at: org.updated_at,
            is_default: preferences?.is_default || false,
            last_accessed: preferences?.last_accessed,
            member_count: 0, // TODO: Get actual member count
            plugin_status: {} // TODO: Get plugin status
          };
        })
      );

      // Sort by default first, then by last accessed
      enrichedWorkspaces.sort((a, b) => {
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;
        if (a.last_accessed && b.last_accessed) {
          return new Date(b.last_accessed).getTime() - new Date(a.last_accessed).getTime();
        }
        return 0;
      });

      setAvailableWorkspaces(enrichedWorkspaces);
      
      // Set current workspace (default or first available)
      const defaultWorkspace = enrichedWorkspaces.find(w => w.is_default) || enrichedWorkspaces[0];
      setCurrentWorkspace(defaultWorkspace);
      
    } catch (err) {
      console.error('Failed to load workspaces:', err);
      setError(err instanceof Error ? err.message : 'Failed to load workspaces');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Switch workspace
  const switchWorkspace = useCallback(async (workspaceId: string) => {
    if (!user?.id) return;

    try {
      // Update default workspace
      await supabase.rpc('set_default_workspace', {
        p_user_id: user.id,
        p_tenant_id: workspaceId
      });

      // Find and set current workspace
      const workspace = availableWorkspaces.find(w => w.id === workspaceId);
      if (workspace) {
        setCurrentWorkspace(workspace);
      }

      // Refresh workspaces to update last_accessed
      await loadWorkspaces();
    } catch (err) {
      console.error('Failed to switch workspace:', err);
      setError('Failed to switch workspace');
    }
  }, [user?.id, availableWorkspaces, loadWorkspaces]);

  // Create new workspace
  const createWorkspace = useCallback(async (name: string, description?: string): Promise<Workspace> => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      const { data, error } = await supabase.rpc('create_organization', {
        p_name: name,
        p_description: description
      });

      if (error) throw error;

      // Refresh workspaces to include new workspace
      await loadWorkspaces();

      // Return the new workspace
      const newWorkspace = availableWorkspaces.find(w => w.id === data);
      if (!newWorkspace) {
        throw new Error('Failed to create workspace');
      }

      return newWorkspace;
    } catch (err) {
      console.error('Failed to create workspace:', err);
      throw err;
    }
  }, [user?.id, availableWorkspaces, loadWorkspaces]);

  // Refresh workspaces
  const refreshWorkspaces = useCallback(async () => {
    await loadWorkspaces();
  }, [loadWorkspaces]);

  // Load workspaces on mount and user change
  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  const contextValue: WorkspaceContextType = {
    currentWorkspace,
    availableWorkspaces,
    isLoading,
    error,
    switchWorkspace,
    refreshWorkspaces,
    createWorkspace
  };

  return (
    <WorkspaceContext.Provider value={contextValue}>
      {children}
    </WorkspaceContext.Provider>
  );
};

// Hook to use workspace context
export const useWorkspaceContext = (): WorkspaceContextType => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspaceContext must be used within a WorkspaceProvider');
  }
  return context;
};

// Hook to get current workspace (convenience)
export const useCurrentWorkspace = (): Workspace | null => {
  const { currentWorkspace } = useWorkspaceContext();
  return currentWorkspace;
};

// Hook to get available workspaces (convenience)
export const useAvailableWorkspaces = (): Workspace[] => {
  const { availableWorkspaces } = useWorkspaceContext();
  return availableWorkspaces;
};

// Hook to switch workspace (convenience)
export const useSwitchWorkspace = () => {
  const { switchWorkspace } = useWorkspaceContext();
  return switchWorkspace;
};




